/**
 * Journey-step renderer for the in-chat journey UX. Mirrors the design
 * from `davidjhillis/agent_views` branch `experience-viewer-video`
 * (learnFlow* functions). Six step types, each appended into the chat
 * transcript as an assistant bubble + the appropriate option UI:
 *
 *   message       → bubble only (server auto-advances on next /chat)
 *   open_ended    → bubble only; visitor uses the regular chat input
 *   single_select → bullet rows; clicking one submits + locks all
 *   multi_select  → toggle rows + Continue (N selected) button
 *   yes_no        → two outlined buttons (yes/no)
 *   action        → CTA buttons (no answer captured server-side)
 *
 * Selecting an option calls `onAnswer(stepId, value)` which the widget
 * wires to POST `/v1/agents/{publicId}/journey-answer` and advances state.
 *
 * Fourth leaf of the incremental Preact migration. The
 * `appendJourneyStep(bodyInner, payload, onAnswer)` API is preserved
 * so ui.tsx is untouched.
 */

import { useState } from 'preact/hooks';
import { mountPreactIsland } from '../voice/preact-mount';

type StepType =
  | 'message'
  | 'open_ended'
  | 'single_select'
  | 'multi_select'
  | 'yes_no'
  | 'action';

type StepOption = { id: string; label: string; value: string };

export type JourneyStepPayload = {
  step: {
    id: string;
    title: string;
    type: StepType;
    prompt: string;
    isRequired?: boolean;
    responseType?: string;
    options?: StepOption[];
    settings?: Record<string, unknown>;
  };
  index: number;
  total: number;
};

export type JourneyAnswerCallback = (
  stepId: string,
  value: string | string[] | null,
) => void;

function SelectHint({ kind }: { kind: 'single' | 'multi' }) {
  return (
    <div class="sp-step-hint">
      <span class="sp-step-hint-dot" />
      <span>
        {kind === 'single'
          ? 'Click any option to continue'
          : 'Select one or more, then continue'}
      </span>
    </div>
  );
}

function YesNo({
  options,
  locked,
  selectedValue,
  onPick,
}: {
  options: StepOption[];
  locked: boolean;
  selectedValue: string | null;
  onPick: (value: string) => void;
}) {
  // Coerce: prefer 2 options; if missing, synthesize Yes/No.
  const pair: StepOption[] =
    options.length >= 2
      ? options.slice(0, 2)
      : [
          { id: 'yes', label: 'Yes', value: 'yes' },
          { id: 'no', label: 'No', value: 'no' },
        ];
  return (
    <div class="sp-step-options sp-step-options-pair">
      {pair.map((o) => (
        <button
          key={o.id}
          type="button"
          class={
            'sp-step-option' +
            (selectedValue === o.value ? ' sp-step-option--selected' : '') +
            (locked ? ' sp-step-option--locked' : '')
          }
          disabled={locked}
          onClick={() => onPick(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SingleSelect({
  options,
  locked,
  selectedValue,
  onPick,
}: {
  options: StepOption[];
  locked: boolean;
  selectedValue: string | null;
  onPick: (value: string) => void;
}) {
  return (
    <div class="sp-step-options sp-step-options-stack">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          class={
            'sp-step-option sp-step-option--single' +
            (selectedValue === o.value ? ' sp-step-option--selected' : '') +
            (locked ? ' sp-step-option--locked' : '')
          }
          disabled={locked}
          onClick={() => onPick(o.value)}
        >
          <span class="sp-step-radio" />
          <span class="sp-step-option-label">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

function MultiSelect({
  options,
  locked,
  onSubmit,
}: {
  options: StepOption[];
  locked: boolean;
  onSubmit: (values: string[]) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (value: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };
  const submit = (): void => {
    if (selected.size === 0) return;
    onSubmit([...selected]);
  };
  return (
    <div class="sp-step-multi-wrap">
      <div class="sp-step-options sp-step-options-stack">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            class={
              'sp-step-option sp-step-option--multi' +
              (selected.has(o.value) ? ' sp-step-option--selected' : '') +
              (locked ? ' sp-step-option--locked' : '')
            }
            disabled={locked}
            onClick={() => toggle(o.value)}
          >
            <span class="sp-step-checkbox" />
            <span class="sp-step-option-label">{o.label}</span>
          </button>
        ))}
      </div>
      <button
        type="button"
        class="sp-step-submit"
        disabled={selected.size === 0 || locked}
        onClick={submit}
      >
        Continue ({selected.size} selected)
      </button>
    </div>
  );
}

function ActionButtons({
  options,
  locked,
  selectedValue,
  onPick,
}: {
  options: StepOption[];
  locked: boolean;
  selectedValue: string | null;
  onPick: (value: string) => void;
}) {
  // Action steps may carry no options (just a CTA paragraph). Fall back
  // to a single "Continue" button so the visitor can move on.
  const items =
    options.length > 0
      ? options
      : [{ id: 'continue', label: 'Continue', value: 'continue' }];
  return (
    <div class="sp-step-options sp-step-options-stack">
      {items.map((o) => (
        <button
          key={o.id}
          type="button"
          class={
            'sp-step-option sp-step-option--action' +
            (selectedValue === o.value ? ' sp-step-option--selected' : '') +
            (locked ? ' sp-step-option--locked' : '')
          }
          disabled={locked}
          onClick={() => onPick(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function JourneyStepView({
  payload,
  onAnswer,
}: {
  payload: JourneyStepPayload;
  onAnswer: JourneyAnswerCallback;
}) {
  const { step, index, total } = payload;
  // Locked = the visitor has submitted (or skipped). Prevents double-
  // submit and shows the locked styling. Once true, never reverts.
  const [locked, setLocked] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const submitOne = (value: string): void => {
    if (locked) return;
    setSelectedValue(value);
    setLocked(true);
    onAnswer(step.id, value);
  };
  const submitMany = (values: string[]): void => {
    if (locked) return;
    setLocked(true);
    onAnswer(step.id, values);
  };
  const skip = (): void => {
    if (locked) return;
    setLocked(true);
    onAnswer(step.id, null);
  };

  // Progress chip — visible commitment marker. Hidden for the first
  // step's `message` type (treated as an intro / value prop) so it
  // doesn't feel like a survey before the visitor opts in.
  const isIntroMessage = step.type === 'message' && index === 1;

  let body;
  switch (step.type) {
    case 'message':
    case 'open_ended':
      body = null;
      break;
    case 'yes_no':
      body = (
        <YesNo
          options={step.options ?? []}
          locked={locked}
          selectedValue={selectedValue}
          onPick={submitOne}
        />
      );
      break;
    case 'single_select':
      body = (
        <>
          <SelectHint kind="single" />
          <SingleSelect
            options={step.options ?? []}
            locked={locked}
            selectedValue={selectedValue}
            onPick={submitOne}
          />
        </>
      );
      break;
    case 'multi_select':
      body = (
        <>
          <SelectHint kind="multi" />
          <MultiSelect
            options={step.options ?? []}
            locked={locked}
            onSubmit={submitMany}
          />
        </>
      );
      break;
    case 'action':
      body = (
        <ActionButtons
          options={step.options ?? []}
          locked={locked}
          selectedValue={selectedValue}
          onPick={submitOne}
        />
      );
      break;
  }

  // Skip affordance — non-required steps with answer-capturing types
  // get a small "Skip" link below the options so visitors aren't
  // forced through every question. `message` and `action` aren't
  // skippable (they don't capture answers anyway).
  const isAnsweringType =
    step.type === 'open_ended' ||
    step.type === 'single_select' ||
    step.type === 'multi_select' ||
    step.type === 'yes_no';
  const showSkip = !step.isRequired && isAnsweringType;

  return (
    <>
      {isIntroMessage ? null : (
        <div class="sp-step-progress">
          Step {index} of {total}
        </div>
      )}
      <div class="sp-msg-text sp-step-bubble">{step.prompt}</div>
      {body}
      {showSkip ? (
        <button
          type="button"
          class="sp-step-skip"
          disabled={locked}
          onClick={skip}
        >
          Skip
        </button>
      ) : null}
    </>
  );
}

/**
 * Append a journey-step bubble to the chat transcript. Returns the
 * outer wrap so the caller can scroll/observe it. The wrapper bubble
 * is created imperatively (parity with the message-bubble convention
 * used elsewhere in ui.tsx); the interactive content inside is a
 * Preact island.
 */
export function appendJourneyStep(
  bodyInner: HTMLElement,
  payload: JourneyStepPayload,
  onAnswer: JourneyAnswerCallback,
): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'sp-msg sp-msg-assistant sp-msg-step';
  wrap.setAttribute('data-step-id', payload.step.id);
  wrap.setAttribute('data-step-type', payload.step.type);
  const content = document.createElement('div');
  content.className = 'sp-msg-content';
  wrap.appendChild(content);
  bodyInner.appendChild(wrap);
  mountPreactIsland(content, <JourneyStepView payload={payload} onAnswer={onAnswer} />);
  return wrap;
}
